import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence, useDragControls, type PanInfo } from 'framer-motion';
import { cn } from '@shared/lib/cn';

/**
 * Bottom-anchored sheet. Slides up 260ms with `smooth` easing; top corners
 * 24px radius + grabber pill. Scrim fades in 180ms.
 *
 * Swipe-to-dismiss: drag the grabber / header downwards — past ~120px or
 * with a quick flick the sheet closes (and `onOpenChange(false)` fires).
 * The scrollable body is not a drag surface, so content still scrolls.
 *
 * Backed by Radix Dialog for focus management, escape-to-dismiss and
 * `role="dialog"` semantics.
 */
interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Optional element rendered on the right of the title row (e.g. a status badge). */
  titleAccessory?: ReactNode;
  description?: string;
  children: ReactNode;
  /** Lock to a specific peak height (CSS length). Default = content height. */
  height?: string;
  /** Optional explicit callback for grabber/header drag dismissals. */
  onDragDismiss?: () => void;
  /** Called after the sheet has fully animated out. */
  onExitComplete?: () => void;
}

/**
 * Whether the on-screen keyboard is up, and how far a sheet has to lift to sit on top of it.
 *
 * A `fixed bottom-0` panel anchors to the LAYOUT viewport, which a mobile browser leaves at
 * full height when the keyboard opens, so a sheet holding a focused input renders underneath
 * it. The VISUAL viewport does shrink, and the difference between the two is the lift.
 *
 * The native apps report the keyboard through the keyboard plugin instead. The iOS app does
 * not resize the WebView (`Keyboard.resize: 'none'`), so the sheet lifts by the keyboard's
 * height; the app-wide keyboard shift cannot help, since it moves `#root` and a sheet is
 * portalled outside it. The Android app resizes the WebView itself, so nothing lifts there,
 * but the sheet still needs to know the keyboard is up to open the same way as on iOS.
 */
function useKeyboard(open: boolean): { shown: boolean; inset: number } {
  const [viewportInset, setViewportInset] = useState(0);
  const [native, setNative] = useState<number | null>(null);

  // 1. Browsers: the gap between the layout and visual viewports.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!open || !viewport) return;

    const measure = () =>
      setViewportInset(Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop));

    measure();
    viewport.addEventListener('resize', measure);
    viewport.addEventListener('scroll', measure);
    return () => {
      viewport.removeEventListener('resize', measure);
      viewport.removeEventListener('scroll', measure);
      setViewportInset(0);
    };
  }, [open]);

  // 2. Native apps: the keyboard plugin, lifting by its height on iOS only.
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    if (!open || platform === 'web') return;

    const shown = Keyboard.addListener('keyboardWillShow', (info) =>
      setNative(platform === 'ios' ? info.keyboardHeight : 0),
    );
    const hidden = Keyboard.addListener('keyboardWillHide', () => setNative(null));
    return () => {
      void shown.then((listener) => listener.remove());
      void hidden.then((listener) => listener.remove());
      setNative(null);
    };
  }, [open]);

  return { shown: native !== null || viewportInset > 0, inset: Math.max(viewportInset, native ?? 0) };
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  titleAccessory,
  description,
  children,
  height,
  onDragDismiss,
  onExitComplete,
}: BottomSheetProps): JSX.Element {
  const dragControls = useDragControls();
  const keyboard = useKeyboard(open);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Once the sheet has opened up above the keyboard, bring the field being typed in into view:
  // it was focused while the keyboard still covered it, so the browser had nothing to scroll.
  useEffect(() => {
    const field = document.activeElement;
    if (keyboard.shown && field instanceof HTMLElement && bodyRef.current?.contains(field)) {
      requestAnimationFrame(() => field.scrollIntoView({ block: 'nearest' }));
    }
  }, [keyboard.shown, keyboard.inset]);

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    if (info.offset.y > 120 || info.velocity.y > 600) {
      onDragDismiss?.();
      onOpenChange(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence onExitComplete={onExitComplete}>
        {open ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild onOpenAutoFocus={(event) => event.preventDefault()}>
              <motion.div
                className={cn(
                  'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-2xl bg-elevated paper-grain shadow-sheet',
                  // The keyboard already covers the home-indicator area.
                  keyboard.shown ? 'pb-0' : 'pb-safe',
                )}
                style={{
                  ...(height ? { height } : null),
                  // Sit on top of the keyboard and fill the height it leaves, so every sheet, short or
                  // tall, opens up the same way and scrolls its own body while the member types.
                  ...(keyboard.shown
                    ? { bottom: keyboard.inset, height: `calc(92dvh - ${keyboard.inset}px)`, maxHeight: 'none' }
                    : null),
                }}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.55 }}
                onDragEnd={handleDragEnd}
              >
                {/* Grabber — drag handle */}
                <div
                  onPointerDown={(e) => dragControls.start(e)}
                  className="flex shrink-0 cursor-grab touch-none items-center justify-center pt-2.5 pb-2 active:cursor-grabbing"
                >
                  <span className="block h-1.5 w-10 rounded-pill bg-hairline-strong" aria-hidden />
                </div>
                {title || description || titleAccessory ? (
                  <header
                    onPointerDown={(e) => dragControls.start(e)}
                    className="shrink-0 cursor-grab touch-none px-6 pb-3 hairline-b active:cursor-grabbing"
                  >
                    {title || titleAccessory ? (
                      <div className="flex items-center justify-between gap-3">
                        {title ? (
                          <Dialog.Title asChild>
                            <h2 className="font-display text-headline text-ink">{title}</h2>
                          </Dialog.Title>
                        ) : (
                          <span />
                        )}
                        {titleAccessory ? <div className="shrink-0">{titleAccessory}</div> : null}
                      </div>
                    ) : null}
                    {description ? (
                      <Dialog.Description asChild>
                        <p className="text-body-sm text-ink-secondary mt-1">{description}</p>
                      </Dialog.Description>
                    ) : null}
                  </header>
                ) : null}
                <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-6 pt-4">{children}</div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}
